import { Theme } from '@fagmobler/sanity';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Countdown } from '../countdown';
import { twMerge } from 'tailwind-merge';
import { IconButton } from '../index';

type TopBannerProps = {
  title?: string;
  cta?: string;
  theme?: Theme;
  type?: 'basic' | 'countdown' | 'countdownLarge';
  countdownEnd?: Date;
  countdownImage?: any;
  NextImage: any;
};

const BasicTopBanner = ({ title, cta }: TopBannerProps) => {
  return (
    <>
      <h2 className="hd-xl">{title}</h2>
      <div className="md:absolute md:right-4 flex items-center cap-base mt-3 md:mt-0">
        <span className="underline">{cta}</span>
        <ArrowRightIcon className="ml-2 h-4" />
      </div>
    </>
  );
};

const CountdownTopBannerSmall = ({
  title,
  cta,
  theme,
  countdownEnd,
}: TopBannerProps) => {
  if (!countdownEnd) {
    return null;
  }
  return (
    <div className="w-full flex flex-col gap-0 items-start">
      <div className="w-full flex flex-row gap-2 lg:gap-4 justify-between">
        {title && (
          <div className="flex flex-col items-start">
            <h2
              className={twMerge(
                clsx(
                  'font-heading font-500 !h-[1.75rem] lg:!h-[5rem] flex items-center justify-start text-left',
                  {
                    'text-[24px] lg:text-[60px] text-nowrap':
                      title.length <= 15,
                    'text-[16px] lg:text-[43px]': title.length > 15,
                  }
                )
              )}
            >
              {title}
            </h2>
            <span className="underline font-heading text-xs block lg:hidden -mt-1">
              {cta}
            </span>
          </div>
        )}
        <Countdown
          size="small"
          countdownEnd={countdownEnd}
          segmentValueClass={clsx({
            'text-neutral-40 bg-neutral-80': theme === 'neutral',
            'text-neutral-80 bg-neutral-0': theme === 'neutralDark',
            'text-brown-30 bg-neutral-80': theme === 'lightBrown',
            'text-tetiery-30 bg-neutral-80': theme === 'lightYellow',
            'text-green-40 bg-neutral-80': theme === 'lightGreen',
            'text-tetiery-60 bg-neutral-80': theme === 'blackWeekYellow',
            'text-neutral-80 bg-tetiery-50': theme === 'blackWeekBlack',
            'text-brown-70 bg-gold-40': theme === 'nobel',
            'text-brown-70 bg-pink-60': theme === 'garden',
            'text-primary-50 bg-neutral-0': theme === 'red',
          })}
          segmentTitleClass={'text-inherit'}
        />
      </div>
      <span className="font-heading underline text-lg hidden lg:block">
        {cta}
      </span>
    </div>
  );
};

const CountdownTopBannerLarge = ({
  title,
  cta,
  theme,
  countdownEnd,
  countdownImage,
  NextImage,
}: TopBannerProps) => {
  if (!countdownEnd) {
    return null;
  }
  return (
    <div className="w-full flex flex-col items-center gap-6 lg:gap-4 group">
      {title && (
        <h2
          className={twMerge(clsx('font-heading text-[40px] lg:text-[100px]'))}
        >
          {title}
        </h2>
      )}
      <Countdown
        size="large"
        countdownEnd={countdownEnd}
        segmentValueClass={clsx({
          'text-neutral-40 bg-neutral-80': theme === 'neutral',
          'text-neutral-80 bg-neutral-0': theme === 'neutralDark',
          'text-brown-30 bg-neutral-80': theme === 'lightBrown',
          'text-tetiery-30 bg-neutral-80': theme === 'lightYellow',
          'text-green-40 bg-neutral-80': theme === 'lightGreen',
          'text-tetiery-60 bg-neutral-80': theme === 'blackWeekYellow',
          'text-neutral-80 bg-tetiery-50': theme === 'blackWeekBlack',
          'text-brown-70 bg-gold-40': theme === 'nobel',
          'text-brown-70 bg-pink-60': theme === 'garden',
          'text-primary-50 bg-neutral-0': theme === 'red',
        })}
        segmentTitleClass={'text-inherit'}
      />
      {countdownImage && (
        <div className="relative h-24 lg:h-56 aspect-video">
          <NextImage
            src={countdownImage.croppedImageUrl}
            alt={countdownImage.alt || title || 'Countdown image'}
            className="object-contain object-center"
            fill
          />
        </div>
      )}
      <div
        className={twMerge(
          clsx('w-full flex flex-row justify-between items-center', {
            '-mt-4 lg:-mt-20': countdownImage,
          })
        )}
      >
        <span className="font-heading text-lg group-hover:underline">
          {cta}
        </span>
        <IconButton variant="ghost" size="md" title={`Gå til ${title}`}>
          <ArrowRightIcon className="w-7 h-7" />
        </IconButton>
      </div>
    </div>
  );
};

export const TopBanner = (props: TopBannerProps) => {
  const { theme = 'neutral', type = 'basic', countdownEnd } = props;

  return (
    <div
      className={clsx(
        'flex items-center justify-center relative p-4 text-center flex-col',
        {
          'p-4': type === 'basic',
          'p-4 lg:p-10': type === 'countdown',
        },
        {
          'bg-neutral-40 text-neutral-80': theme === 'neutral',
          'text-neutral-0 bg-neutral-80': theme === 'neutralDark',
          'bg-brown-30 text-neutral-80': theme === 'lightBrown',
          'bg-tetiery-30 text-neutral-80': theme === 'lightYellow',
          'bg-green-40 text-neutral-80': theme === 'lightGreen',
          'bg-tetiery-60 text-neutral-80': theme === 'blackWeekYellow',
          'bg-neutral-80 text-tetiery-50': theme === 'blackWeekBlack',
          'bg-brown-70 text-gold-40': theme === 'nobel',
          'bg-brown-70 text-pink-60': theme === 'garden',
          'bg-primary-50 text-neutral-0': theme === 'red',
        }
      )}
    >
      {type === 'countdown' && countdownEnd && (
        <CountdownTopBannerSmall {...props} />
      )}
      {type === 'countdownLarge' && countdownEnd && (
        <CountdownTopBannerLarge {...props} />
      )}
      {type === 'basic' && <BasicTopBanner {...props} />}
    </div>
  );
};
